import { sql } from "drizzle-orm";
import {
  boolean,
  index,
  integer,
  jsonb,
  pgEnum,
  pgTable,
  text,
  timestamp,
  uuid,
} from "drizzle-orm/pg-core";
import { orders } from "./order";

export const itemSourceEnum = pgEnum("item_source", ["OWNED", "MARKETPLACE", "CATALOG"]);

export const orderLineStatusEnum = pgEnum("order_line_status", ["PENDING", "PACKED", "CANCELLED"]);

export const orderLines = pgTable(
  "order_lines",
  {
    id: uuid("id").primaryKey().defaultRandom(),
    orderId: uuid("order_id")
      .notNull()
      .references(() => orders.id),
    itemSource: itemSourceEnum("item_source").notNull(),
    itemId: text("item_id").notNull(),
    /** The platform's own inventory id. No FK: external platforms send their own ids. */
    inventoryItemId: text("inventory_item_id"),
    snapshot: jsonb("snapshot").notNull(),
    unitValue: integer("unit_value").notNull(),
    fulfillmentFee: integer("fulfillment_fee"),
    isSlowShip: boolean("is_slow_ship").notNull().default(false),
    status: orderLineStatusEnum("status").notNull().default("PENDING"),
    /**
     * Whether this line took stock when it was written (`reserveStock`). `false` marks a
     * record-only line (a tenant mirroring its own order record): its pack / ship / cancel must
     * not touch stock either. `null` predates the column and is treated as reserved.
     */
    stockReserved: boolean("stock_reserved"),
    /**
     * What the fulfiller paid the vendor for this line, USD cents: for a StockX line the checkout
     * total for the chosen size, tax included, shipping excluded. Set when the order moves
     * PENDING to PROCESSING; null before that and on lines nobody buys. Never sent to a tenant.
     */
    paidCostCents: integer("paid_cost_cents"),
    /**
     * The StockX size refund owed to the shopper, USD cents, computed with `paidCostCents` at
     * the same transition. Null until then and on lines that get no refund computed.
     */
    refundAmountCents: integer("refund_amount_cents"),
    createdAt: timestamp("created_at", { withTimezone: true }).notNull().defaultNow(),
    updatedAt: timestamp("updated_at", { withTimezone: true }).notNull().defaultNow(),
  },
  (t) => [
    // Every read of an order's lines, and every join from orders to its lines, filters on this;
    // Postgres does not index a foreign key by itself.
    index("order_lines_order_id_idx").on(t.orderId),
    // "Is this unit already in a live order?": the withdrawal route and the sell-back guard.
    // Partial, because mirrored and catalog lines carry no inventory item.
    index("order_lines_inventory_item_idx")
      .on(t.inventoryItemId)
      .where(sql`${t.inventoryItemId} is not null`),
  ],
);
